import React, { JSX, ReactNode } from 'react'
import { Link } from 'react-router'
import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { BILLING_PATH } from 'lib/paths'
import { LoadingSpinner } from 'components/Loading'
import { useEntitlements } from 'context/plan'
import { useEventAccess } from 'hooks/useEventAccess'
import { PlanFeatureKey } from 'types/api'

const FEATURE_COPY: Record<PlanFeatureKey, { title: string, body: string }> = {
  interactiveContent: {
    title: 'Interactive content is not on your plan',
    body: 'Polls, tug-of-wars and scoreboards let your chat vote live on screen. Your current plan does not include them, so this section is read-only.'
  }
}

const tierLabel = (tier: string): string =>
  tier.length === 0 ? tier : tier.charAt(0).toUpperCase() + tier.slice(1)

const GatePanel = ({ children }: { children: ReactNode }): JSX.Element => (
  <div className='mt-6 px-8 pb-8'>
    <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
      {children}
    </section>
  </div>
)

export const EntitlementGate = ({
  feature,
  children
}: {
  feature: PlanFeatureKey
  children: ReactNode
}): JSX.Element => {
  const { status, error, tier, staffUncapped, featureFor, upgradeForFeature, refetch } = useEntitlements()

  if (staffUncapped) return <>{children}</>

  if (status === 'loading') {
    return (
      <div data-testid='entitlement-gate-loading' className='flex flex-1 items-center justify-center'>
        <LoadingSpinner />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <GatePanel>
        <div
          data-testid='entitlement-gate-error'
          className='flex flex-col items-center justify-center gap-3 py-12 text-center'
        >
          <ExclamationTriangleIcon className='size-8 text-red-400' aria-hidden='true' />
          <p className='max-w-sm text-sm text-red-300'>
            {error ?? 'Could not load your plan.'}
          </p>
          <button
            type='button'
            onClick={() => { void refetch() }}
            className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            Retry
          </button>
        </div>
      </GatePanel>
    )
  }

  if (featureFor(feature)) return <>{children}</>

  const copy = FEATURE_COPY[feature]
  const upgrade = upgradeForFeature(feature)

  return (
    <GatePanel>
      <div
        data-testid='entitlement-gate-locked'
        className='flex flex-col items-center justify-center gap-3 py-12 text-center'
      >
        <LockClosedIcon className='size-10 text-dm-text-accent' aria-hidden='true' />
        <h3 className='text-base font-semibold text-dm-text-primary'>{copy.title}</h3>
        <p className='max-w-md text-sm text-dm-text-secondary'>{copy.body}</p>
        {tier !== null && (
          <p className='text-xs text-dm-text-accent'>
            You are on <span className='font-semibold text-dm-text-secondary'>{tierLabel(tier)}</span>.
          </p>
        )}
        {upgrade !== null && (
          <p className='max-w-md text-sm text-dm-text-secondary'>
            Included on <span className='font-semibold text-dm-text-primary'>{upgrade.label ?? tierLabel(upgrade.tier)}</span> and up.
          </p>
        )}
        <Link
          to={BILLING_PATH}
          data-testid='entitlement-gate-view-plans'
          className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
        >
          View plans
        </Link>
      </div>
    </GatePanel>
  )
}

export const EventEntitlementGate = ({
  feature,
  children
}: {
  feature: PlanFeatureKey
  children: ReactNode
}): JSX.Element => {
  const { role } = useEventAccess()

  if (role !== null && role !== 'owner') return <>{children}</>

  return <EntitlementGate feature={feature}>{children}</EntitlementGate>
}
